import React, { useState } from 'react';
import { ModelsState, ModelType } from './types';
import ModelTabs from './ModelTabs';
import ModelPairView from './ModelPairView';
import { LoadingSpinner, GenericErrorBanner } from '../../common';
import { useAdminTranslation } from '../../../modules/i18n/hooks/useTranslation';

interface ModelsContentProps {
  modelsState: ModelsState;
  onOpenSettings: (type: ModelType) => void;
  onClearError: () => void;
}

const ModelsContent: React.FC<ModelsContentProps> = ({ modelsState, onOpenSettings, onClearError }) => {
  const { t } = useAdminTranslation();
  const [activeTab, setActiveTab] = useState<ModelType>('embedding');

  if (modelsState.loading) {
    return (
      <div className='flex h-64 items-center justify-center'>
        <LoadingSpinner />
      </div>
    );
  }

  // Get current config based on active tab
  const getCurrentConfig = () => {
    switch (activeTab) {
      case 'embedding':
        return modelsState.embedding;
      case 'ranking':
        return modelsState.ranking;
      case 'llm':
        return modelsState.llm;
    }
  };

  return (
    <div className='flex h-full flex-col'>
      {/* Header */}
      <div className='mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between'>
        <div>
          <h2 className='text-lg font-medium text-gray-900 dark:text-dark-text'>{t('models.title')}</h2>
          <p className='mt-1 text-sm text-gray-500 dark:text-dark-text-secondary hidden md:block'>{t('models.description')}</p>
        </div>
      </div>

      {/* Error banner */}
      {modelsState.error && (
        <div className='mb-4'>
          <GenericErrorBanner message={modelsState.error} onClose={onClearError} />
        </div>
      )}

      {/* Navigation Tabs - Sticky */}
      <div className='sticky top-0 z-10 -mx-1 bg-white px-1 dark:bg-dark-background'>
        <ModelTabs activeTab={activeTab} onTabChange={setActiveTab} />
      </div>

      {/* Main Content - Scrollable */}
      <div className='min-h-0 flex-1 overflow-y-auto scrollbar-thin pt-4'>
        <div className='animate-fade-in'>
          <ModelPairView config={getCurrentConfig()} onOpenSettings={onOpenSettings} />
        </div>
      </div>
    </div>
  );
};

export default ModelsContent;